import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import { createKey } from '@/api/useApiQuery';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { useTenant } from '@/providers/TenantProvider';

export type EvaluationRow = {
  metric_name: string;
  value: number | null;
  lead_time: number | null;
  span: number | null;
  primary_keys: Record<string, unknown>;
};

export type EvaluationQueryResponse = { results: EvaluationRow[] };

// Pull the first numeric value out of an evaluation response (the queries here
// are scoped to a single series, so there's at most one meaningful row).
export function firstEvalValue(results: EvaluationRow[] | undefined): number | null {
  const v = results?.find((r) => typeof r.value === 'number')?.value;
  return typeof v === 'number' ? v : null;
}

const EVAL_FILE_ID = 'evaluation_metrics.parquet';

// One evaluation run per model: the window is now carried entirely by the metric
// name (1wk / 1mo / 3mo), so a single run holds all three windows for a model.
// TODO(engineer): hardcoded eval run UUIDs, one per model — same root cause as the other
// eval-run constants in this feature (no dynamic run resolution for eval jobs yet).
// Follow-up: once solutions-service eval-run resolution exists, replace evalRunId()
// with a lookup by model + fct instead of these fixed constants.
const BFG_EVAL_RUN_ID = '27e703d5-ca0b-4ed6-a566-6f8d31d02e3a';
const ETS_EVAL_RUN_ID = 'd64e43dd-4598-4405-8c31-6ca81fba7a93';

export function evalRunId(model: EvalModel): string {
  return model === 'bfg' ? BFG_EVAL_RUN_ID : ETS_EVAL_RUN_ID;
}

// Metric bases, per model. New scheme: `{BASE}_{window}_per_series`. Coverage
// differs by model — BFG emits the p10–p90 interval, ETS emits p50.
export type EvalMetricKey = 'wape' | 'bias' | 'pinball' | 'coverage';

const EVAL_METRIC_BASE: Record<EvalModel, Record<EvalMetricKey, string>> = {
  bfg: {
    wape: 'WAPE',
    bias: 'NET_BIAS',
    pinball: 'PINBALL_p50',
    coverage: 'COVERAGE_p10_p90',
  },
  incumbent: {
    wape: 'WAPE',
    bias: 'NET_BIAS',
    pinball: 'PINBALL_p50',
    coverage: 'COVERAGE_p50',
  },
};

// Table windows. The window token is embedded in the metric name (1wk/1mo/3mo);
// fct is the forecast-creation date that backs each window's WAPE callout.
export type EvalWindowKey = 'week' | 'month' | 'quarter';

const WINDOW_TOKEN: Record<EvalWindowKey, string> = {
  week: '1wk',
  month: '1mo',
  quarter: '3mo',
};

export const EVAL_WINDOWS: Record<EvalWindowKey, { label: string; fct: string }> = {
  week: { label: '1 Week WAPE', fct: '2025-04-27' },
  month: { label: '1 Month WAPE', fct: '2025-03-30' },
  quarter: { label: '3 Month WAPE', fct: '2025-02-02' },
};

export function evalMetricName(model: EvalModel, metric: EvalMetricKey, window: EvalWindowKey): string {
  return `${EVAL_METRIC_BASE[model][metric]}_${WINDOW_TOKEN[window]}_per_series`;
}

export type EvalModel = 'bfg' | 'incumbent';

// The currently-selected product/customer/region, shaped as evaluation
// primary_keys (e.g. { product_id, ship_to_cust_id, ship_to_region }).
export function useEvaluationPrimaryKeys(): Record<string, string> {
  const { grains } = useTenant();
  const { state } = useForecastQueryState();
  return useMemo(
    () => ({
      ...(grains.product && state.product ? { [grains.product]: state.product } : {}),
      ...(grains.ship_to && state.customer ? { [grains.ship_to]: state.customer } : {}),
      ...(grains.ship_to_geography ? { [grains.ship_to_geography]: state.shipToGeography || 'North America' } : {}),
    }),
    [grains.product, grains.ship_to, grains.ship_to_geography, state.product, state.customer, state.shipToGeography],
  );
}

const EVAL_PK_BATCH_SIZE = 900;

function chunk<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [items];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// Fetches evaluation rows for a (run, metric) over a list of PK filters, split
// into WAF-safe batches and merged. Returns the same { data, isLoading, isError }
// shape callers relied on from useApiQuery, so it's a drop-in replacement.
// batchSize defaults to EVAL_PK_BATCH_SIZE; pass a non-positive value (0) to
// send every PK in a single request (no batching).
export function useEvaluationQuery(args: {
  runId: string;
  metricName: string;
  primaryKeys: Array<Record<string, string>>;
  enabled?: boolean;
  batchSize?: number;
}): { data: EvaluationQueryResponse | undefined; isLoading: boolean; isError: boolean } {
  const { runId, metricName, primaryKeys, enabled = true, batchSize = EVAL_PK_BATCH_SIZE } = args;
  const { tenantAlias } = useTenant();

  const filters = primaryKeys.filter((pk) => Object.keys(pk).length > 0);
  // chunk() with size <= 0 returns a single batch (all filters), i.e. no batching.
  const batches = useMemo(() => chunk(filters, batchSize), [filters, batchSize]);

  const queries = useQueries({
    queries: batches.map((batch) => {
      const body = {
        run_id: runId,
        group_id: 'default',
        file_id: EVAL_FILE_ID,
        metric_name: metricName,
        primary_keys: batch,
      };
      return {
        queryKey: createKey(tenantAlias, ApiEndpoint.ModelOutputsEvaluation, { body }),
        queryFn: () => apiFetch<EvaluationQueryResponse>(tenantAlias, ApiEndpoint.ModelOutputsEvaluation, { body }),
        enabled: enabled && filters.length > 0,
      };
    }),
  });

  // useQueries returns a fresh array each render, so don't memoize on it
  // (@tanstack/query/no-unstable-deps). The merge below is a cheap pass over a
  // handful of batches, so compute it inline each render rather than memoizing.
  const hasData = queries.some((q) => q.data);
  const data = hasData ? { results: queries.flatMap((q) => q.data?.results ?? []) } : undefined;

  return {
    data,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
}

export function useEvaluationMetric(args: { model: EvalModel; metric: EvalMetricKey; window: EvalWindowKey }) {
  const { model, metric, window } = args;
  const primaryKeys = useEvaluationPrimaryKeys();

  return useEvaluationQuery({
    runId: evalRunId(model),
    metricName: evalMetricName(model, metric, window),
    // Single series (the current product/customer selection) → one-element list.
    primaryKeys: [primaryKeys],
  });
}
